import { AdaptiveMenu } from '@picsart/design-system/components/AdaptiveMenu';
import { BaseBottomSheet } from '@picsart/design-system/components/BaseBottomSheet';
import { PanelHeading } from '@picsart/design-system/components/PanelHeading';
import { InlineButton } from '@picsart/design-system/components/InlineButton';
import { IconCloudUpload } from '@picsart/design-system/foundation/icons/IconCloudUpload';
import { IconFolderOutline } from '@picsart/design-system/foundation/icons/IconFolderOutline';
import { IconCanvas } from '@picsart/design-system/foundation/icons/IconCanvas';
import { useTranslator } from '@picsart/rc/services/localization';
import { useDeviceType } from '@picsart/design-system/hooks/device';
import useStyles from './styles';
import type { UploadSourceMenuProps } from './types';

/**
 * Menu component for selecting upload source.
 * Options: From Device, From Picsart Drive, From Canvas.
 * Branches by viewport: BottomSheet on mobile, AdaptiveMenu (anchored dropdown)
 * on tablet/desktop. AdaptiveMenu's built-in branch uses user-agent detection
 * which doesn't match small viewports on tablet-class UAs.
 */
const UploadSourceMenu = ({
  reference,
  isOpen,
  onClose,
  onSelectFromDevice,
  onSelectFromDrive,
  onSelectFromCanvas,
  hasCanvasImages,
}: UploadSourceMenuProps) => {
  const classes = useStyles();
  const translate = useTranslator();
  const { isDesktop } = useDeviceType();

  const handleItemClick = (callback: () => void) => () => {
    onClose();
    callback();
  };

  const menuItems = (
    <div className={classes.menu}>
      <InlineButton
        dataTestId="upload-source-menu-device"
        size="large"
        startIcon={IconCloudUpload}
        alignment="start"
        isFullWidth
        isWithoutBorder
        isWithoutBackground
        onClick={handleItemClick(onSelectFromDevice)}
      >
        {translate('From Device')}
      </InlineButton>

      {onSelectFromDrive && (
        <InlineButton
          dataTestId="upload-source-menu-drive"
          size="large"
          startIcon={IconFolderOutline}
          alignment="start"
          isFullWidth
          isWithoutBorder
          isWithoutBackground
          onClick={handleItemClick(onSelectFromDrive)}
        >
          {translate('From Picsart Drive')}
        </InlineButton>
      )}

      {hasCanvasImages && onSelectFromCanvas && (
        <InlineButton
          dataTestId="upload-source-menu-canvas"
          size="large"
          startIcon={IconCanvas}
          alignment="start"
          isFullWidth
          isWithoutBorder
          isWithoutBackground
          onClick={handleItemClick(onSelectFromCanvas)}
        >
          {translate('From Canvas')}
        </InlineButton>
      )}
    </div>
  );

  if (!isDesktop) {
    // Three menu rows + header — compact fixed height matches working DS examples.
    const SHEET_HEIGHT = 280;
    return (
      <>
        {reference}
        <BaseBottomSheet
          isOpen={isOpen}
          onClose={onClose}
          snapPoints={[SHEET_HEIGHT]}
          defaultActiveIndex={0}
          zIndex={3000}
        >
          <div className={classes.sheetHeading}>
            <PanelHeading
              title={translate('Upload from')}
              closeAction={onClose}
            />
          </div>
          {menuItems}
        </BaseBottomSheet>
      </>
    );
  }

  return (
    <AdaptiveMenu
      dataTestId="upload-source-menu"
      isVisible={isOpen}
      handleClose={onClose}
      title={translate('Upload from')}
      reference={reference}
    >
      {menuItems}
    </AdaptiveMenu>
  );
};

export default UploadSourceMenu;
